// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import QtyField, { QtyFieldStyles } from "@ces/App/Components/Qty/QtyField";
import xUseQty from "@ces/App/Hooks/xUseQty";
import { CustomAppState } from "@ces/App/Store/Reducers";
import { MAX_QTY } from "@ces/XDL/constants";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import {
    HasProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { TextFieldProps } from "@insite/mobius/TextField";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: QtyFieldStyles;
    displayTooltip?: boolean;
    // XNG-Changes
    showLabel?: boolean;
    isDebounceDisabled?: boolean;
}

const mapStateToProps = (
    state: CustomAppState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
        // XNG-Changes
    } & Omit<HasProductContext, "productContext"> & {
            productContext: Omit<ProductContextModel, "productInfo"> & {
                productInfo: ProductInfo & { selectedUOMLineId?: string };
            };
        },
) => {
    // XNG-Changes
    const {
        product: { id: productId, unitOfMeasures },
        onQtyOrderedChanged,
        productInfo: { unitOfMeasure, selectedUOMLineId },
    } = props.productContext;
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        // XNG-Changes
        qtyPerBaseUnitOfMeasure:
            state.xSelectedUom.quickOrderUomIds[(selectedUOMLineId || productId) + unitOfMeasure] ||
            state.xSelectedUom.multiplesOfByProductId[selectedUOMLineId || productId] ||
            state.xSelectedUom.multiplesOfByProductId[`Auto-Complete-${productId}`] ||
            unitOfMeasures?.find(uom => uom.unitOfMeasure === unitOfMeasure && !uom.isDefault)
                ?.qtyPerBaseUnitOfMeasure ||
            unitOfMeasures?.find(uom => !uom.isDefault)?.qtyPerBaseUnitOfMeasure ||
            unitOfMeasures?.find(uom => uom.isDefault)?.qtyPerBaseUnitOfMeasure,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

// export const productQuantityOrderedStyles: TextFieldPresentationProps = {
//     cssOverrides: {
//         formInputWrapper: css`
//             input[type="number"]::-webkit-outer-spin-button,
//             input[type="number"]::-webkit-inner-spin-button {
//                 /* ISC-11023 deal with this. */
//                 /* stylelint-disable-next-line */
//                 -webkit-appearance: none;
//                 margin: 0;
//             }
//         `,
//         inputSelect: css`
//             /* ISC-11023 deal with this. */
//             /* stylelint-disable-next-line */
//             -moz-appearance: textfield;
//         `,
//     },
// };
export const productQuantityOrderedStyles: QtyFieldStyles = {};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, detail, minimumOrderQty = 1 },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    // XNG-Change
    displayTooltip = true,
    showLabel = false,
    qtyPerBaseUnitOfMeasure,
    isDebounceDisabled,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());
    const [minQtyState, setMinQty] = useState(1);
    const [maxQtyState, setMaxQty] = useState(MAX_QTY);

    const showQtyTooltip =
        ((minimumOrderQty && minimumOrderQty > 1) || (detail?.multipleSaleQty && detail?.multipleSaleQty > 1)) &&
        displayTooltip;
    // XNG-Changes
    const packageSize = Number(qtyPerBaseUnitOfMeasure || detail?.multipleSaleQty || 1);
    // Xng-Changes
    useEffect(() => {
        if (packageSize > 1) {
            const maxQty = MAX_QTY - (MAX_QTY % packageSize);
            setMaxQty(maxQty);
        }
    }, [packageSize]);

    // Xng-Changes
    const { handleChange, debouncedHandleChange } = xUseQty({
        minQtyState,
        multipleSaleQty: packageSize > 1 ? packageSize : undefined,
        onQtyOrderedChanged,
        productId,
        setProductQty,
        timeout: isDebounceDisabled && !showQtyTooltip ? 0 : 300,
    });

    // Xng-Changes
    useEffect(() => {
        const minQty = Math.max(minimumOrderQty, 1);

        if (!isDebounceDisabled) {
            const qty =
                qtyOrdered === 1 && detail?.multipleSaleQty && detail?.multipleSaleQty > 1
                    ? Math.max(minQty, detail?.multipleSaleQty)
                    : Math.max(minQty, qtyOrdered);
            debouncedHandleChange(qty.toString());
        }

        if (qtyOrdered === 1 && packageSize > 1 && isDebounceDisabled) {
            handleChange(Math.max(minQty, packageSize).toString());
        }

        setMinQty(minQty);
    }, [minimumOrderQty, detail?.multipleSaleQty, qtyPerBaseUnitOfMeasure, isDebounceDisabled]);

    // Xng-Changes
    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }

        if (!isDebounceDisabled) {
            debouncedHandleChange(`${Math.max(minimumOrderQty || 1, qtyOrdered) || 1}`);
        } else {
            setProductQty(`${Math.max(minimumOrderQty || 1, qtyOrdered) || 1}`);
        }
    }, [qtyOrdered]);

    // XNG-Change Show disabled
    // if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
    //     return null;
    // }
    const isDisabled = (!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart;

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    // Xng-Changes
    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement> | string) => {
        let value = typeof event === "string" ? event : event.target.value;
        // XNG-Changes: reset to minimum / package size when input is zero
        if (Number(value) === 0) {
            value = Math.max(packageSize, minQtyState).toString();
        }
        setProductQty(value);
        onQtyOrderedChanged?.(+value);
    };

    // Xng-Changes
    const blurHandler = () => {
        if (Number(productQty) < minQtyState) {
            setProductQty(`${minQtyState}`);
            debouncedHandleChange(`${minQtyState}`);
        } else {
            handleChange(productQty);
        }
    };

    // XNG-Change use QtyField
    return (
        // <TextField
        //     label={labelOverride ?? translate("QTY")}
        //     value={productQty}
        //     type="number"
        //     min={0}
        //     max={999999}
        //     data-test-selector="product_qtyOrdered"
        //     {...otherProps}
        //     onKeyPress={keyPressHandler}
        //     onChange={qtyChangeHandler}
        //     {...styles}
        // />
        <QtyField
            disabled={isDisabled}
            label={labelOverride}
            showLabel={showLabel}
            value={productQty}
            type="qty"
            data-test-selector="product_qtyOrdered"
            {...otherProps}
            onKeyPress={keyPressHandler}
            onChange={qtyChangeHandler}
            onBlur={blurHandler}
            showMultipleSaleQtyTooltip={!!showQtyTooltip}
            minimumOrderQty={minimumOrderQty}
            multipleSaleQty={detail?.multipleSaleQty}
            extendedStyles={styles}
            min={Math.max(minQtyState, packageSize)}
            // XNG-Changes
            qtyPerBaseUnitOfMeasure={qtyPerBaseUnitOfMeasure}
            max={maxQtyState}
        />
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
